import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getWalletTransactions } from "../api/walletTransactions.api";
import type {
  WalletTransaction,
  WalletTransactionSummary,
} from "../types/walletTransactions.types";
const size = 20;
export function useWalletTransactions() {
  const [items, setItems] = useState<WalletTransaction[]>([]);
  const [summary, setSummary] = useState<WalletTransactionSummary | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [name, setName] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [error, setError] = useState<string | null>(null);
  const { run, loading } = useLatestRequest();
  const refetch = useCallback(async () => {
    setError(null);
    try {
      const response = await run((signal) =>
        getWalletTransactions(
          {
            index: page - 1,
            size,
            ...(name ? { name } : {}),
            ...(type !== "all" ? { type } : {}),
            ...(status !== "all" ? { status } : {}),
            ...(source !== "all" ? { source } : {}),
          },
          signal,
        ),
      );
      if (response) {
        setItems(response.items);
        setSummary(response.summary);
        setTotalCount(response.totalCount);
      }
    } catch (requestError: unknown) {
      setItems([]);
      setSummary(null);
      setTotalCount(0);
      setError(getApiErrorMessage(requestError, "Failed to load wallet transactions"));
    }
  }, [name, page, run, source, status, type]);
  useEffect(() => {
    const timer = window.setTimeout(() => void refetch(), 300);
    return () => window.clearTimeout(timer);
  }, [refetch]);
  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };
  return {
    items,
    summary,
    totalCount,
    page,
    name,
    type,
    status,
    source,
    error,
    loading,
    refetch,
    setPage,
    setName: change(setName),
    setType: change(setType),
    setStatus: change(setStatus),
    setSource: change(setSource),
    size,
  };
}
